import React, { useContext, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import {
  FiHome,
  FiTruck,
  FiAnchor,
  FiSend,
  FiDollarSign,
  FiFileText,
  FiLogOut,
  FiChevronDown,
  FiBox,
  FiUsers,
  FiSettings,
} from 'react-icons/fi';

const Sidebar = () => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const [expandedSection, setExpandedSection] = useState(null);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  const isExactOrChild = (path) => location.pathname === path || location.pathname.startsWith(path);
  const isMenuOpen = (section) => expandedSection === section;

  const toggleSection = (section) => {
    setExpandedSection(isMenuOpen(section) ? null : section);
  };

  const handleLogout = () => {
    logout();
  };

  // Operations menu items config
  const operations = [
    {
      key: 'truck',
      label: 'Truck',
      icon: <FiTruck />,
      listPath: '/truck',
      newPath: '/truck/new',
      listLabel: 'Shipment List',
      newLabel: 'New Shipment',
    },
    {
      key: 'ocean-import',
      label: 'Ocean Import',
      icon: <FiAnchor />,
      listPath: '/ocean-import',
      newPath: '/ocean-import/new',
      listLabel: 'Shipment List',
      newLabel: 'New Shipment',
    },
    {
      key: 'ocean-export',
      label: 'Ocean Export',
      icon: <FiSend />,
      listPath: '/ocean-export',
      newPath: '/ocean-export/new',
      listLabel: 'Shipment List',
      newLabel: 'New Shipment',
    },
    {
      key: 'air-import',
      label: 'Air Import',
      icon: <FiSend style={{ transform: 'rotate(-45deg)' }} />,
      listPath: '/air-import',
      newPath: '/air-import/new',
      listLabel: 'Shipment List',
      newLabel: 'New Shipment',
    },
    {
      key: 'air-export',
      label: 'Air Export',
      icon: <FiSend style={{ transform: 'rotate(45deg)' }} />,
      listPath: '/air-export',
      newPath: '/air-export/new',
      listLabel: 'Shipment List',
      newLabel: 'New Shipment',
    },
    {
      key: 'misc',
      label: 'Misc',
      icon: <FiBox />,
      listPath: '/misc',
      newPath: '/misc/new',
      listLabel: 'Shipment List',
      newLabel: 'New Shipment',
    },
    {
      key: 'isf',
      label: 'ISF',
      icon: <FiFileText />,
      listPath: '/isf',
      newPath: '/isf/new',
      listLabel: 'ISF List',
      newLabel: 'New ISF',
    },
  ];

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <div className="sidebar-logo">
          <span style={{ fontSize: '20px' }}>📦</span>
        </div>
        <span className="sidebar-logo">MyLogistic</span>
      </div>

      <nav className="sidebar-nav">
        {/* Dashboard */}
        <div className="nav-section">
          <Link
            to="/"
            className={`nav-item ${isActive('/dashboard') || location.pathname === '/' ? 'active' : ''}`}
          >
            <FiHome />
            <span>Dashboard</span>
          </Link>
        </div>

        {/* Operations */}
        <div className="nav-section">
          <div className="nav-section-title">Operations</div>
          {operations.map(op => (
            <React.Fragment key={op.key}>
              <div
                className={`nav-item ${isExactOrChild(op.listPath) ? 'active' : ''}`}
                onClick={() => toggleSection(op.key)}
                style={{ cursor: 'pointer' }}
              >
                {op.icon}
                <span>{op.label}</span>
                <FiChevronDown
                  style={{
                    marginLeft: 'auto',
                    transform: isMenuOpen(op.key) ? 'rotate(180deg)' : '',
                    transition: 'transform 0.2s',
                  }}
                />
              </div>
              <div className={`nav-submenu ${isMenuOpen(op.key) ? 'open' : ''}`}>
                <Link
                  to={op.listPath}
                  className={`nav-submenu-item ${isExactOrChild(op.listPath) ? 'active' : ''}`}
                >
                  {op.listLabel}
                </Link>
                <Link
                  to={op.newPath}
                  className={`nav-submenu-item ${location.pathname === op.newPath ? 'active' : ''}`}
                >
                  {op.newLabel}
                </Link>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Trade Partner */}
        <div className="nav-section">
          <div className="nav-section-title">Trade Partner</div>
          <div
            className={`nav-item ${isExactOrChild('/trade-partner') ? 'active' : ''}`}
            onClick={() => toggleSection('trade-partner')}
            style={{ cursor: 'pointer' }}
          >
            <FiUsers />
            <span>Trade Partner</span>
            <FiChevronDown style={{ marginLeft: 'auto', transform: isMenuOpen('trade-partner') ? 'rotate(180deg)' : '', transition: 'transform 0.2s' }} />
          </div>
          <div className={`nav-submenu ${isMenuOpen('trade-partner') ? 'open' : ''}`}>
            <Link
              to="/trade-partner"
              className={`nav-submenu-item ${location.pathname === '/trade-partner' ? 'active' : ''}`}
            >
              Trade Partner List
            </Link>
            <Link
              to="/trade-partner/new"
              className={`nav-submenu-item ${location.pathname === '/trade-partner/new' ? 'active' : ''}`}
            >
              New Trade Partner
            </Link>
          </div>
        </div>

        {/* Accounting */}
        <div className="nav-section">
          <div className="nav-section-title">Accounting</div>
          <div
            className={`nav-item ${isExactOrChild('/invoice-list') || isExactOrChild('/cost-list') || isExactOrChild('/invoice-cost') || isExactOrChild('/profit-report') ? 'active' : ''}`}
            onClick={() => toggleSection('accounting')}
            style={{ cursor: 'pointer' }}
          >
            <FiDollarSign />
            <span>Invoice / Cost</span>
            <FiChevronDown style={{ marginLeft: 'auto', transform: isMenuOpen('accounting') ? 'rotate(180deg)' : '', transition: 'transform 0.2s' }} />
          </div>
          <div className={`nav-submenu ${isMenuOpen('accounting') ? 'open' : ''}`}>
            <Link
              to="/invoice-list"
              className={`nav-submenu-item ${isActive('/invoice-list') ? 'active' : ''}`}
            >
              Invoice List (A/R)
            </Link>
            <Link
              to="/cost-list"
              className={`nav-submenu-item ${isActive('/cost-list') ? 'active' : ''}`}
            >
              Cost List (A/P)
            </Link>
            <Link
              to="/profit-report"
              className={`nav-submenu-item ${isActive('/profit-report') ? 'active' : ''}`}
            >
              Profit Report
            </Link>
          </div>

          {/* Payment */}
          <div
            className={`nav-item ${isExactOrChild('/payment') ? 'active' : ''}`}
            onClick={() => toggleSection('payment')}
            style={{ cursor: 'pointer' }}
          >
            <FiDollarSign />
            <span>Payment</span>
            <FiChevronDown style={{ marginLeft: 'auto', transform: isMenuOpen('payment') ? 'rotate(180deg)' : '', transition: 'transform 0.2s' }} />
          </div>
          <div className={`nav-submenu ${isMenuOpen('payment') ? 'open' : ''}`}>
            <Link
              to="/payment/new?mode=receive"
              className={`nav-submenu-item ${location.pathname === '/payment/new' && location.search.includes('receive') ? 'active' : ''}`}
            >
              Receive Payment
            </Link>
            <Link
              to="/payment/new?mode=make"
              className={`nav-submenu-item ${location.pathname === '/payment/new' && location.search.includes('make') ? 'active' : ''}`}
            >
              Make Payment
            </Link>
            <Link
              to="/payment-received-list"
              className={`nav-submenu-item ${isActive('/payment-received-list') ? 'active' : ''}`}
            >
              Payment Received List
            </Link>
            <Link
              to="/payment-made-list"
              className={`nav-submenu-item ${isActive('/payment-made-list') ? 'active' : ''}`}
            >
              Payment Made List
            </Link>
          </div>

          {/* Bank */}
          <Link
            to="/bank-reconciliation"
            className={`nav-item ${isActive('/bank-reconciliation') ? 'active' : ''}`}
          >
            <FiDollarSign />
            <span>Bank Reconciliation</span>
          </Link>

          {/* Accounting Reports */}
          <div
            className={`nav-item ${isExactOrChild('/balance-sheet') || isExactOrChild('/income-statement') || isExactOrChild('/aging-report') || isExactOrChild('/customer-statement') ? 'active' : ''}`}
            onClick={() => toggleSection('acct-reports')}
            style={{ cursor: 'pointer' }}
          >
            <FiFileText />
            <span>Report</span>
            <FiChevronDown style={{ marginLeft: 'auto', transform: isMenuOpen('acct-reports') ? 'rotate(180deg)' : '', transition: 'transform 0.2s' }} />
          </div>
          <div className={`nav-submenu ${isMenuOpen('acct-reports') ? 'open' : ''}`}>
            <Link to="/balance-sheet" className={`nav-submenu-item ${isActive('/balance-sheet') ? 'active' : ''}`}>
              Balance Sheet
            </Link>
            <Link to="/income-statement" className={`nav-submenu-item ${isActive('/income-statement') ? 'active' : ''}`}>
              Income Statement
            </Link>
            <Link to="/aging-report" className={`nav-submenu-item ${isActive('/aging-report') ? 'active' : ''}`}>
              Aging Report
            </Link>
            <Link to="/customer-statement" className={`nav-submenu-item ${isActive('/customer-statement') ? 'active' : ''}`}>
              Customer Statement
            </Link>
          </div>
        </div>

        {/* Reports */}
        <div className="nav-section">
          <div className="nav-section-title">Analytics</div>
          <Link
            to="/reports"
            className={`nav-item ${isActive('/reports') ? 'active' : ''}`}
          >
            <FiFileText />
            <span>Reports</span>
          </Link>
        </div>

        {/* Admin */}
        {user?.role === 'admin' && (
          <div className="nav-section">
            <div className="nav-section-title">Admin</div>
            <Link to="/users" className={`nav-item ${isActive('/users') ? 'active' : ''}`}>
              <FiSettings />
              <span>User Management</span>
            </Link>
            <Link to="/activity-log" className={`nav-item ${isActive('/activity-log') ? 'active' : ''}`}>
              <FiFileText />
              <span>Activity Log</span>
            </Link>
          </div>
        )}
      </nav>

      <div className="sidebar-footer">
        <span className="user-info" title={user?.name || 'User'}>
          {user?.name || 'User'}
        </span>
        <button className="logout-btn" onClick={handleLogout} title="Logout">
          <FiLogOut />
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
